import { FC, ReactNode } from "react";
import LinkVariant from "../../../../components/link/@types/LinkVariant";
import Link from "../../../../components/link/Link";
import { NetworkClassification } from "../../@types/common";
import { useNetworkRoute } from "../../hooks/useNetworkRoute";
import { DOWNLOADED_FILES_URL_PARAM } from "../../ThreatIntel/Url/hooks/useDownloadedFilesClassification";

type NetworkLinkParams = {
  downloadedFilesClassification?: NetworkClassification;
};

type Props = {
  className?: string;
  path: string;
  fragment?: string;
  children: ReactNode;
  variant?: LinkVariant;
  "data-testid"?: string;
  params?: NetworkLinkParams;
};

/**
 * React router has a bug where it fully decodes multiple URL encoded values
 * It was an existing issue for useParams in versions prior to 6.22.1 but it still has
 * the same issue on Link component
 *
 * This component corrects the issue by manually encoding the value
 */

export const getRoute = (
  path: string,
  type?: string,
  value?: string,
  fragment?: string,
  networkParams?: NetworkLinkParams
) => {
  if (!type || !value) return "";
  const encodedUrl = encodeURIComponent(value);
  const baseRoute = `/${type}/${encodedUrl}/${path}`;

  const params = new URLSearchParams();
  if (networkParams?.downloadedFilesClassification) {
    params.append(
      DOWNLOADED_FILES_URL_PARAM,
      networkParams.downloadedFilesClassification
    );
  }

  const queryString = params.toString();
  return `${baseRoute}${queryString ? `?${queryString}` : ""}${
    fragment ? `#${fragment}` : ""
  }`;
};

const NetworkLink: FC<Props> = ({
  path,
  className,
  fragment,
  children,
  variant,
  "data-testid": dataTestId,
  params
}) => {
  const [type, value] = useNetworkRoute();

  const route = getRoute(path, type, value, fragment, params);

  return (
    <Link
      className={className}
      to={route}
      variant={variant}
      data-testid={dataTestId}
    >
      {children}
    </Link>
  );
};

export default NetworkLink;
